// Account list contracts — FE-038.
//
// FE-038 ships the page chrome ahead of the dedicated REST surface
// (BE-052 owns the canonical ``/api/v1/accounts/`` endpoint). Until
// that lands the page reads from the BE-029 ``client_performance``
// endpoint, which already returns per-Account rows with the metrics
// the table needs. ``mapClientPerformanceRow`` in ``api.ts`` adapts
// the dashboard shape into this Account-list contract so the swap
// to BE-052 is a one-line change.

export type AccountListSortKey =
  | "legal_name"
  | "open_commitments"
  | "overdue_commitments"
  | "renewal_date"
  | "updated_at";


export interface AccountListFilters {
  search?: string;
  status?: "active" | "archived" | "all";
  /** Risk bucket from the dashboard drill-through. */
  risk_level?: "low" | "medium" | "high" | "critical";
  has_overdue?: boolean;
  going_cold?: boolean;
  renewal_month?: string;
  renewal_status?: "on_track" | "behind" | "at_risk" | "not_started";
  renewal_attention?: boolean;
  /** Account-detail pin from the dashboard's
   * ``buildAccountFilterUrl({ id })`` shape. */
  id?: string;
  // Accounts-01 — segmentation filters.
  primary_location_id?: string;
  naics_sector?: string;
  naics_code?: string;
  risk_class?: AccountRiskClass;
  account_tag_id?: string;
  account_tag_slug?: string;
}


export type AccountRiskClass = "office" | "mixed" | "manual";


export interface AccountTagSummary {
  tag_id: string;
  name: string;
  slug: string;
}


// ── Accounts-Teams-01 — Client team ──────────────────────────────────────


export interface AccountTeamMember {
  id: string;
  membership_id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  display_name: string;
  email: string;
  title: string;
  is_producer: boolean;
  is_account_lead: boolean;
  status: string;
  added_at: string | null;
}


export interface AccountTeamListResponse {
  rows: AccountTeamMember[];
  total: number;
}


export interface AddAccountTeamMemberInput {
  membership_id: string;
  is_account_lead?: boolean;
}


export interface AccountPrimaryLocationSummary {
  location_id: string;
  name: string;
  city: string | null;
  state_code: string | null;
  status: "active" | "inactive";
}


export interface NaicsSectorOption {
  /** Canonical sector code: 2-digit, "31-33" / "44-45" / "48-49" range,
   * or the literal "unknown" sentinel. */
  code: string;
  label: string;
}


export interface NaicsSearchResult {
  code: string;
  title: string;
  sector_code: string;
  sector_label: string;
  description_snippet: string;
}


export interface AccountListQuery {
  filters: AccountListFilters;
  sortBy: AccountListSortKey;
  sortDir: "asc" | "desc";
  limit: number;
  offset: number;
}


export interface AccountListRow {
  /** Stable Account UUID. */
  account_id: string;
  /** Visible legal name; "DBA" rendered next to it when present. */
  legal_name: string;
  dba_name?: string | null;
  status: "active" | "archived";
  industry?: string | null;
  employee_count_total?: number | null;
  employee_count_eligible?: number | null;
  primary_contact_name?: string | null;
  primary_contact_email?: string | null;
  current_plan_year_id?: string | null;
  renewal_date?: string | null;
  open_commitments?: number | null;
  overdue_commitments?: number | null;
  blocked_commitments?: number | null;
  completion_rate?: number | null;
  on_time_percent?: number | null;
  risk_bucket?: "low" | "medium" | "high" | "critical" | null;
  updated_at?: string | null;
  // Accounts-01 — segmentation surface (scalars on list rows; full
  // ``primary_location`` + ``account_tags`` arrive on the detail
  // payload).
  primary_location_id?: string | null;
  naics_sector?: string | null;
  naics_code?: string | null;
  risk_class?: AccountRiskClass | null;
}


export interface AccountListResponse {
  rows: AccountListRow[];
  total: number;
}


// ── Account detail ──────────────────────────────────────────────────────


export interface AccountTouchpointSummary {
  id: string;
  kind: string;
  body: string;
  occurred_at: string;
}


export interface AccountFileSummary {
  id: string;
  filename: string;
  size_bytes?: number | null;
  uploaded_at?: string | null;
}


export interface AccountCommitmentSummary {
  id: string;
  title: string;
  status: string;
  due_date_time?: string | null;
  is_blocked: boolean;
  assigned_to_display_name?: string | null;
}


export interface AccountDetail extends AccountListRow {
  /** ISO date the BOR was signed; surfaced in the header when present. */
  bor_date?: string | null;
  /** ISO datetime returned by the canonical endpoint as ``bor_signed_at``. */
  bor_signed_at?: string | null;
  ein?: string | null;
  primary_address_line1?: string | null;
  primary_address_line2?: string | null;
  primary_address_city?: string | null;
  primary_address_state?: string | null;
  primary_address_zip?: string | null;
  notes?: string | null;
  notes_document_id?: string | null;
  /** Recent Commitments — empty when no list endpoint is wired. */
  recent_commitments: AccountCommitmentSummary[];
  /** Recent Touchpoints — empty when no list endpoint is wired. */
  recent_touchpoints: AccountTouchpointSummary[];
  /** Linked Files — empty when no list endpoint is wired. */
  linked_files: AccountFileSummary[];
  /**
   * Internal tag for the data source. ``accounts_endpoint`` is the
   * canonical BE-052 surface; ``temporary_client_performance`` is the
   * legacy fallback that doesn't carry the editable form fields.
   */
  source: "temporary_client_performance" | "accounts_endpoint";
  // Accounts-01 — segmentation block on the detail payload.
  primary_location?: AccountPrimaryLocationSummary | null;
  naics_title?: string | null;
  account_tags?: AccountTagSummary[];
  // Accounts-Teams-01 — Client team summary embedded on detail.
  client_team?: AccountTeamMember[];
}


export class AccountNotFoundError extends Error {
  constructor(public readonly accountId: string) {
    super(`Account ${accountId} not found`);
    this.name = "AccountNotFoundError";
  }
}


// ── Accounts-Contacts-01 — Account contacts ─────────────────────────────


export interface AccountContact {
  contact_id: string;
  account_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  mobile_phone: string | null;
  job_title: string | null;
  department: string | null;
  is_primary: boolean;
  notes: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}


export interface AccountContactListResponse {
  rows: AccountContact[];
  total: number;
}


export interface AccountContactInput {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  mobile_phone?: string | null;
  job_title?: string | null;
  department?: string | null;
  is_primary?: boolean;
  notes?: string | null;
}


export type AccountContactPatch = Partial<AccountContactInput>;


// ── Account form (FE-040) ───────────────────────────────────────────────


export type AccountFormMode = "create" | "edit";


/** Controlled-input form state. Strings stay strings so the inputs
 * don't have to special-case null/undefined; the API helper converts
 * back to nullable primitives at submit time. */
export interface AccountFormValues {
  legal_name: string;
  dba_name: string;
  ein: string;
  industry: string;
  employee_count_total: string;
  employee_count_eligible: string;
  bor_signed_at: string;
  primary_address_line1: string;
  primary_address_line2: string;
  primary_address_city: string;
  primary_address_state: string;
  primary_address_zip: string;
  notes: string;
  // Accounts-01 — segmentation form state.
  primary_location_id: string;
  naics_sector: string;
  naics_code: string;
  /** Cached human-readable label for the selected ``naics_code`` so
   * the form can render a chip without a refetch. */
  naics_title: string;
  risk_class: "" | AccountRiskClass;
  account_tag_ids: string[];
}


export interface CreateAccountInput {
  legal_name: string;
  dba_name?: string | null;
  ein?: string | null;
  industry?: string | null;
  employee_count_total?: number | null;
  employee_count_eligible?: number | null;
  bor_signed_at?: string | null;
  primary_address_line1?: string | null;
  primary_address_line2?: string | null;
  primary_address_city?: string | null;
  primary_address_state?: string | null;
  primary_address_zip?: string | null;
  notes?: string | null;
  notes_document_id?: string | null;
  // Accounts-01 — segmentation create payload.
  primary_location_id?: string | null;
  naics_sector?: string | null;
  naics_code?: string | null;
  risk_class?: AccountRiskClass | null;
  account_tag_ids?: string[];
}


export type UpdateAccountInput = Partial<CreateAccountInput>;


export interface AccountMutationResponse {
  account_id: string;
}
